import { AlertCircle, CheckCircle2, Download, FileSpreadsheet, Trash2, Upload, X } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import LoadingSpinner from '../../../../components/LoadingSpinner';

import {
  fetchOutreachDatabaseCounts,
  importOutreachFile,
  type ImportProgress,
  type ImportRunResult,
  type OutreachDatabaseCounts,
} from './outreachImportService';
import {
  downloadOutreachCsvTemplate,
  parseImportFile,
  type ParsedImportFile,
} from './outreachImportUtils';

interface OutreachImportModalProps {
  onClose: () => void;
  onImported: () => void;
}

function CountsLine({ label, counts }: { label: string; counts: OutreachDatabaseCounts | null }) {
  if (!counts) return null;
  return (
    <p className="text-[12px]">
      <strong>{label}:</strong>{' '}
      {counts.persons.toLocaleString()} persons · {counts.with_email.toLocaleString()} with email ·{' '}
      {counts.import_batches} batches
    </p>
  );
}

function phaseLabel(phase: ImportProgress['phase']): string {
  switch (phase) {
    case 'loading_existing': return 'Loading existing records…';
    case 'wiping': return 'Clearing existing records…';
    case 'importing': return 'Importing…';
    case 'cancelled': return 'Import cancelled';
    case 'done': return 'Import complete';
    default: return 'Processing…';
  }
}

export function OutreachImportModal({ onClose, onImported }: OutreachImportModalProps) {
  const [parsed, setParsed]             = useState<ParsedImportFile | null>(null);
  const [parseErr, setParseErr]           = useState<string | null>(null);
  const [importing, setImporting]         = useState(false);
  const [result, setResult]               = useState<ImportRunResult | null>(null);
  const [replaceAll, setReplaceAll]       = useState(false);
  const [dbCounts, setDbCounts]           = useState<OutreachDatabaseCounts | null>(null);
  const [progress, setProgress]           = useState<ImportProgress | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !importing) onClose();
    };
    document.addEventListener('keydown', h);
    document.body.style.overflow = 'hidden';
    void fetchOutreachDatabaseCounts().then(setDbCounts);
    return () => {
      document.removeEventListener('keydown', h);
      document.body.style.overflow = '';
    };
  }, [onClose, importing]);

  const handleFile = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setParseErr(null);
    setResult(null);
    setParsed(null);
    try {
      const data = await parseImportFile(file);
      if (data.sheets.length === 0) {
        setParseErr('No valid rows found. Check headers match the template (Name / Email Id or Customer name / Contact person).');
        return;
      }
      setParsed(data);
    } catch (err) {
      setParseErr(err instanceof Error ? err.message : 'Could not parse file.');
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  }, []);

  const handleCancelImport = () => {
    abortRef.current?.abort();
  };

  const handleImport = async () => {
    if (!parsed) return;
    if (replaceAll && dbCounts && dbCounts.persons > 0) {
      const ok = window.confirm(
        `This will DELETE all ${dbCounts.persons.toLocaleString()} existing outreach records and re-import from file. Continue?`,
      );
      if (!ok) return;
    }
    setImporting(true);
    setParseErr(null);
    setResult(null);
    setProgress(null);
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const run = await importOutreachFile(
        parsed,
        { replaceAll },
        {
          signal: controller.signal,
          onProgress: setProgress,
        },
      );
      setResult(run);
      setDbCounts(run.databaseAfter);
      if (run.totalImported > 0 || run.replacedExisting) {
        onImported();
      }
    } catch (err) {
      setParseErr(err instanceof Error ? err.message : 'Import failed.');
    } finally {
      setImporting(false);
      abortRef.current = null;
    }
  };

  const previewRows = parsed?.sheets.flatMap(s =>
    s.rows.slice(0, 3).map(r => ({ ...r, _sheet: s.sheetName, _format: s.detectedFormat })),
  ) ?? [];

  const totalRows = parsed?.sheets.reduce((n, s) => n + s.rows.length, 0) ?? 0;
  const withEmail = parsed?.sheets.flatMap(s => s.rows).filter(r => r.email).length ?? 0;
  const pct = progress && progress.total > 0
    ? Math.min(100, Math.round((progress.processed / progress.total) * 100))
    : 0;

  return createPortal(
    <>
      <div
        aria-hidden
        className="fixed inset-0 z-40 bg-black/50 backdrop-blur-[2px]"
        onClick={importing ? undefined : onClose}
      />
      <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
        <div
          className="w-full sm:max-w-[640px] max-h-[92vh] sm:max-h-[88vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl flex flex-col"
          style={{
            background: 'var(--surface-card-elevated, var(--vscode-bg-elevated))',
            border: '1px solid var(--vscode-border-primary)',
          }}
          onClick={e => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-5 py-4 border-b shrink-0"
               style={{ borderColor: 'var(--vscode-border-secondary)' }}>
            <div>
              <h2 className="text-[15px] font-semibold" style={{ color: 'var(--vscode-text-primary)' }}>
                Import outreach list
              </h2>
              <p className="text-[12px] mt-0.5" style={{ color: 'var(--vscode-text-secondary)' }}>
                Excel (.xlsx, .xls) or CSV — existing emails skipped on re-import
              </p>
            </div>
            {!importing && (
              <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:opacity-70 min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation">
                <X className="w-4 h-4" style={{ color: 'var(--vscode-text-secondary)' }} />
              </button>
            )}
          </div>

          <div className="p-5 space-y-4">
            {dbCounts && dbCounts.persons > 0 && !importing && (
              <div className="rounded-lg px-3 py-2 text-[12px]"
                   style={{ background: 'var(--vscode-bg-secondary)', color: 'var(--vscode-text-secondary)' }}>
                <CountsLine label="Database now" counts={dbCounts} />
              </div>
            )}

            {!importing && !result && (
              <>
                <div
                  className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors hover:opacity-80 touch-manipulation"
                  style={{ borderColor: 'var(--vscode-border-primary)', background: 'var(--vscode-bg-secondary)' }}
                  onClick={() => fileRef.current?.click()}
                >
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                    onChange={e => void handleFile(e)}
                    className="hidden"
                  />
                  <FileSpreadsheet className="w-7 h-7 mx-auto mb-2" style={{ color: 'var(--vscode-text-tertiary)' }} />
                  <p className="text-[13px] font-medium" style={{ color: 'var(--vscode-text-secondary)' }}>
                    Click to choose CSV or Excel file
                  </p>
                </div>

                <button
                  type="button"
                  onClick={downloadOutreachCsvTemplate}
                  className="inline-flex items-center gap-1.5 text-[12px] underline underline-offset-2 touch-manipulation min-h-[44px]"
                  style={{ color: 'var(--vscode-text-tertiary)' }}
                >
                  <Download className="w-3.5 h-3.5" />
                  Download CSV template
                </button>
              </>
            )}

            {parseErr && (
              <p className="text-[12.5px] px-3 py-2 rounded-lg flex items-start gap-2"
                 style={{ background: 'rgba(239,68,68,0.08)', color: '#b91c1c', border: '1px solid rgba(239,68,68,0.2)' }}>
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                {parseErr}
              </p>
            )}

            {importing && progress && (
              <div className="rounded-xl p-5 space-y-4"
                   style={{ background: 'var(--vscode-bg-secondary)', border: '1px solid var(--vscode-border-secondary)' }}>
                <div className="flex items-center gap-3">
                  <LoadingSpinner size="sm" showText={false} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold" style={{ color: 'var(--vscode-text-primary)' }}>
                      {phaseLabel(progress.phase)}
                    </p>
                    {progress.currentSheet && progress.phase === 'importing' && (
                      <p className="text-[11px] truncate mt-0.5" style={{ color: 'var(--vscode-text-tertiary)' }}>
                        Sheet: {progress.currentSheet}
                      </p>
                    )}
                  </div>
                </div>

                <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--vscode-border-secondary)' }}>
                  <div
                    className="h-full rounded-full transition-all duration-300"
                    style={{
                      width: `${pct}%`,
                      background: 'var(--brand-purple, #6C47FF)',
                    }}
                  />
                </div>

                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[12px]" style={{ color: 'var(--vscode-text-secondary)' }}>
                  <span>Processed</span>
                  <span className="font-semibold text-right tabular-nums">
                    {progress.processed.toLocaleString()} / {progress.total.toLocaleString()}
                  </span>
                  <span>Imported successfully</span>
                  <span className="font-semibold text-right tabular-nums" style={{ color: '#059669' }}>
                    {progress.imported.toLocaleString()}
                  </span>
                  <span>Duplicates skipped</span>
                  <span className="font-semibold text-right tabular-nums">{progress.duplicates.toLocaleString()}</span>
                  <span>Errors</span>
                  <span className="font-semibold text-right tabular-nums">{progress.errors.toLocaleString()}</span>
                </div>

                <p className="text-[11px]" style={{ color: 'var(--vscode-text-tertiary)' }}>
                  Records already imported are kept if you cancel. Only remaining rows are skipped.
                </p>

                <button
                  type="button"
                  onClick={handleCancelImport}
                  className="w-full py-2.5 rounded-xl text-[13px] font-medium min-h-[44px] touch-manipulation"
                  style={{ border: '1px solid var(--vscode-border-primary)', color: 'var(--vscode-text-secondary)' }}
                >
                  Cancel import
                </button>
              </div>
            )}

            {parsed && !result && !importing && (
              <>
                <div className="text-[12px] space-y-1" style={{ color: 'var(--vscode-text-secondary)' }}>
                  <p><strong>{parsed.filename}</strong> · {parsed.fileFormat.toUpperCase()} · {parsed.sheets.length} sheet(s) · <strong>{totalRows.toLocaleString()}</strong> rows in file</p>
                  <p>{withEmail.toLocaleString()} with email · {(totalRows - withEmail).toLocaleString()} without email</p>
                </div>

                <label className="flex items-start gap-2.5 cursor-pointer select-none rounded-lg px-3 py-2.5 touch-manipulation"
                       style={{ background: replaceAll ? 'rgba(239,68,68,0.08)' : 'var(--vscode-bg-secondary)', border: `1px solid ${replaceAll ? 'rgba(239,68,68,0.25)' : 'var(--vscode-border-secondary)'}` }}>
                  <input
                    type="checkbox"
                    checked={replaceAll}
                    onChange={e => setReplaceAll(e.target.checked)}
                    className="mt-0.5 w-4 h-4 accent-red-600"
                  />
                  <span className="text-[12px]" style={{ color: 'var(--vscode-text-secondary)' }}>
                    <strong style={{ color: replaceAll ? '#b91c1c' : 'var(--vscode-text-primary)' }}>
                      Replace all — delete existing records first
                    </strong>
                    <span className="block mt-0.5 opacity-80">
                      Use for a clean re-import. Clears all {dbCounts?.persons.toLocaleString() ?? '…'} persons, then imports fresh.
                    </span>
                  </span>
                </label>

                <div className="flex gap-2 pt-1">
                  <button type="button" onClick={onClose}
                    className="flex-1 py-2.5 rounded-xl text-[13px] font-medium min-h-[44px] touch-manipulation"
                    style={{ border: '1px solid var(--vscode-border-primary)', color: 'var(--vscode-text-secondary)' }}>
                    Cancel
                  </button>
                  <button type="button" onClick={() => void handleImport()}
                    className="flex-1 py-2.5 rounded-xl text-[13px] font-semibold min-h-[44px] touch-manipulation flex items-center justify-center gap-2"
                    style={{ background: replaceAll ? '#dc2626' : 'var(--brand-purple, #6C47FF)', color: '#fff' }}>
                    {replaceAll ? (
                      <><Trash2 className="w-4 h-4" /> Replace & import {totalRows.toLocaleString()}</>
                    ) : (
                      <><Upload className="w-4 h-4" /> Import {totalRows.toLocaleString()}</>
                    )}
                  </button>
                </div>
              </>
            )}

            {result && (
              <div className="rounded-xl p-4 text-[13px] space-y-3"
                   style={{
                     background: result.cancelled ? 'rgba(245,158,11,0.08)' : 'rgba(16,185,129,0.08)',
                     border: `1px solid ${result.cancelled ? 'rgba(245,158,11,0.25)' : 'rgba(16,185,129,0.25)'}`,
                     color: result.cancelled ? '#92400e' : '#065f46',
                   }}>
                <div className="flex items-center gap-2 font-semibold">
                  <CheckCircle2 className="w-4 h-4" />
                  {result.cancelled ? 'Import stopped — partial results saved' : 'Import summary'}
                </div>

                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[12px]">
                  <span>Rows in file</span><span className="font-semibold text-right">{result.rowsInFile.toLocaleString()}</span>
                  <span>New imported</span><span className="font-semibold text-right">{result.totalImported.toLocaleString()}</span>
                  <span>Duplicates skipped</span><span className="font-semibold text-right">{result.totalDuplicates.toLocaleString()}</span>
                  <span>Errors</span><span className="font-semibold text-right">{result.totalErrors.toLocaleString()}</span>
                  <span>Skipped</span><span className="font-semibold text-right">{result.totalSkipped.toLocaleString()}</span>
                  {result.replacedExisting && (
                    <>
                      <span>Mode</span><span className="font-semibold text-right">Replace all</span>
                    </>
                  )}
                  {result.cancelled && (
                    <>
                      <span>Status</span><span className="font-semibold text-right">Cancelled</span>
                    </>
                  )}
                </div>

                <div className="border-t pt-2 space-y-1" style={{ borderColor: result.cancelled ? 'rgba(245,158,11,0.2)' : 'rgba(16,185,129,0.2)' }}>
                  <CountsLine label="Database before" counts={result.databaseBefore} />
                  <CountsLine label="Database after" counts={result.databaseAfter} />
                </div>

                <ul className="text-[11px] opacity-90 space-y-0.5 border-t pt-2" style={{ borderColor: result.cancelled ? 'rgba(245,158,11,0.2)' : 'rgba(16,185,129,0.2)' }}>
                  {result.sheets.map(s => (
                    <li key={s.sheetName}>
                      {s.sheetName}: {s.rowsInFile.toLocaleString()} in file → {s.imported.toLocaleString()} new, {s.duplicates.toLocaleString()} dupes, {s.errors} errors
                    </li>
                  ))}
                </ul>

                <button type="button" onClick={onClose}
                  className="w-full py-2 rounded-lg text-[12px] font-medium touch-manipulation"
                  style={{ background: result.cancelled ? 'rgba(245,158,11,0.15)' : 'rgba(16,185,129,0.15)' }}>
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

export default OutreachImportModal;
